import React, {Fragment, useState, useEffect, useCallback, useRef} from 'react'
import {ReactComponent as ChevronRight} from '../../assets/icons/chevron-right.svg'
import {ReactComponent as ArrowBack} from '../../assets/icons/arrow_back.svg'
import {useSelectedVariantContext} from './context/SelectedVariantContext'
import {
    actionChangeDisplay,
    actionChangeSelectedLayer,
    actionSetRunningImageCombine,
    actionSetRunningImageCombinedMode
} from './context/SelectedVariantContext/action'
import {toaster} from '../../shared/PFToast'
import {isBackofficeEnv} from '../../helpers/isBackoffceEnv'
import Publish from './publish/Publish'
import { useEditorStageSides } from '../../hooks/useEditorStageSides'
import { StepIndicator } from '../shopify-mapping/header/MappingHeader'
import { useNavigate } from 'react-router-dom'
import { Tooltip } from 'reactstrap'
import {mergeAttributesByDesignEdited} from './context/SelectedVariantContext/helper'
import {usePreviewArtworks} from '../../hooks/usePreviewArtworks'
import DesignModeSelector from './DesignModeSelector'
import { DESIGN_MODES } from './Step2Container'
import PubSub from './Broker'
import {COVERAGE_EVENTS} from './coverage-events'

const NavBar = ({mappingProductId, variantId, sku, showDesignModeTooltip}) => {
    const {
        state: {display, pickerAttributes, productDetail, runningImageCompress, productCreatedType, cropMode, designs, mappingProduct, product, designMode, currentBgImage, applyBgAllSide, selectedSide},
        dispatch,
    } = useSelectedVariantContext()
    const {fetchCapturedSides} = usePreviewArtworks()
    const navigate = useNavigate()
    const [tooltipOpen, setTooltipOpen] = useState(false)
    const [coverageWarnings, setCoverageWarnings] = useState({})
    const toggleTooltip = () => setTooltipOpen(!tooltipOpen)
    const stageSides = useEditorStageSides()

    // Subscribe to artwork coverage check results from ImageLayer
    useEffect(() => {
        const token = PubSub.subscribe(COVERAGE_EVENTS.CHECK, (data) => {
            const {side, result} = data || {}
            if (side && result) {
                setCoverageWarnings((prev) => ({...prev, [side]: result}))
            }
        })
        return () => PubSub.unsubscribe(token)
    }, [])

    // Invalidate coverage warnings when layers are added to non-active sides
    // (e.g. via "Apply to all areas"). Those sides need re-verification before Next.
    const prevLayerCountsRef = useRef(null)
    useEffect(() => {
        const currentCounts = {}
        for (const group of stageSides) {
            for (const stage of group) {
                currentCounts[stage.side] = (stage.layers || []).length
            }
        }

        const prev = prevLayerCountsRef.current
        if (prev) {
            const invalidated = []
            for (const [side, count] of Object.entries(currentCounts)) {
                const prevCount = prev[side] ?? 0
                // Layer added to a side that is NOT currently active — its coverage is stale
                if (count > prevCount && side !== selectedSide) {
                    invalidated.push(side)
                }
            }
            if (invalidated.length > 0) {
                setCoverageWarnings((prev) => {
                    const next = {...prev}
                    for (const side of invalidated) delete next[side]
                    return next
                })
            }
        }

        prevLayerCountsRef.current = currentCounts
    }, [stageSides, selectedSide])

    const hasLayers = stageSides.some((side) =>
        side.some((stage) => (stage.layers?.length > 0 && (designMode === DESIGN_MODES.COMBINED ? stage.side === 'all-in-one' : stage.side !== 'all-in-one')))
    )

    const isDesignStep = display === 'design'
    const isPreviewStep = display === 'preview'
    const isPricingStep = display === 'pricing'

    const toggleDisplay = (_display) => {
        if (_display !== display) {
            dispatch(actionChangeDisplay(_display))
        }
    }

    // Returns true if coverage check blocked navigation (warning published), false if all clear.
    const isCoverageBlocked = useCallback(() => {
        const mockupVersion = product?.mockup_type || product?.mockups?.[0]?.meta?.version
        const shouldVerifyCoverage = ['v1', 'v2', 'v3'].includes(mockupVersion)
        if (!shouldVerifyCoverage || currentBgImage?.file) return false

        const allRelevantSides = new Set()
        for (const attrGroup of stageSides) {
            for (const stage of attrGroup) {
                const sideName = stage.side
                if (designMode === DESIGN_MODES.COMBINED && sideName !== 'all-in-one') continue
                if (designMode !== DESIGN_MODES.COMBINED && sideName === 'all-in-one') continue
                allRelevantSides.add(sideName)
            }
        }

        const failedSides = [...allRelevantSides].filter((side) => {
            // isUserSetFill is propagated to all sides by handleChangeApplyAllPlacements when applyBgAllSide is on,
            // so just checking isUserSetFill covers both single-side and apply-all cases
            const stageBg = stageSides.flatMap((g) => g).find((s) => s.side === side)?.background
            if (stageBg?.isUserSetFill) return false
            const result = coverageWarnings[side]
            return !result || !result.ok
        })

        if (failedSides.length > 0) {
            PubSub.publish(COVERAGE_EVENTS.BLOCK_NEXT, [{failedSides}])
            return true
        }

        return false
    }, [product, currentBgImage, stageSides, designMode, applyBgAllSide, coverageWarnings])

    const switchPreview = () => {
        let invalidDpi = null
        for (const attr of pickerAttributes) {
            const {safeZone} = attr

            for (const [key, value] of Object.entries(safeZone)) {
                const totalInvalidDpi = value.invalidsDpi.length
                if (totalInvalidDpi) {
                    invalidDpi = {
                        side: key,
                        total: totalInvalidDpi,
                    }
                    break
                }
            }
        }
        if (invalidDpi) {
            toaster({
                type: 'error',
                message: `Side ${invalidDpi.side} has ${invalidDpi.total} layer image invalid DPI`,
            })
        } else {
            if (isCoverageBlocked()) return

            // All sides passed — clear any previously shown warning banner before navigating
            PubSub.publish(COVERAGE_EVENTS.CLEAR, [{}])

            dispatch(actionChangeSelectedLayer(null))
            toggleDisplay('preview')
            if (designMode === DESIGN_MODES.COMBINED) {
                dispatch(actionSetRunningImageCombinedMode())
                designs.forEach(design => {
                    design.side['all-in-one'].isSideEdited = true
                    design.isDesignEdited = true
                })
            } else {
                dispatch(actionSetRunningImageCombine(true))
            }
            const fetchedDesigns = mergeAttributesByDesignEdited(pickerAttributes, designs)
            fetchCapturedSides(fetchedDesigns, true)
        }
    }

    const backToProductList = () => {
        const confirmed = window.confirm('Changes you made may not be saved.')
        if (confirmed) {
            if (mappingProductId) {
                navigate(`/mapping-variants/${mappingProductId}?variantId=${variantId}`)
            }
            else if (isBackofficeEnv()) {
                window.open(`${process.env.REACT_APP_BASE_URL}/a/products/mockup-generator/variants`, '_self')
            } else {
                window.open(`${process.env.REACT_APP_WEBSITE_URL}/product/${productDetail.slug}`, '_self')
            }
        }
    }

    const handleClickNextBtn = () => {
        switchPreview()
    }

    const handleNavigate = () => {
        if (mappingProductId) {
            backToMapping()
            return
        }
        return isPreviewStep || isPricingStep ? toggleDisplay('design') : backToProductList()
    }

    const backToMapping = () => {
        navigate(`/mapping-variants/${mappingProductId}?variantId=${variantId}`, {replace: true})
    }

    const renderSaveButton = () => {
        if (mappingProductId && mappingProduct) {
            let variant = product.variants?.find((v) => v.sku === sku)
            if (!variant) {
                variant = mappingProduct.variant
            }
            let title = variant.attributes?.map((opt) => opt.value_text).join(' / ') || ''
            let color = null
            const colorOption = variant?.attributes?.find((attr) => attr.type === 'color')
            if (colorOption) {
                color = colorOption.color_code || colorOption.value_code
            }
            return (
                <Fragment>
                    <StepIndicator activeStep={2} />
                    <div className="attribute" id="variant-info-tooltip">
                        Variant:&nbsp;
                        {color && <span className="colorPreview" style={{'--bg-color': color}} />}
                        <span className="variant">{title || ''}</span>
                        <span className="price">${variant.base_cost || 0}</span>
                    </div>
                    <Tooltip
                        placement="bottom"
                        isOpen={tooltipOpen}
                        target="variant-info-tooltip"
                        toggle={toggleTooltip}
                    >
                        {title || 'No variant title'}
                    </Tooltip>
                    {isDesignStep && <DesignModeSelector showDesignModeTooltip={showDesignModeTooltip} />}
                    <Publish disabled={!hasLayers} />
                </Fragment>
            )
        }
        return (
            <Fragment>
                {isDesignStep && <DesignModeSelector showDesignModeTooltip={showDesignModeTooltip} />}
                {isDesignStep &&
                productCreatedType !== 'regularHasTemplate' &&
                productCreatedType !== 'regularNoTemplate' ? (
                    <button
                        id={'header_next_btn'}
                        type={'button'}
                        className={'Design btn btn-primary flex-center rounded'}
                        disabled={runningImageCompress || !hasLayers || cropMode}
                        onClick={handleClickNextBtn}
                    >
                        Next <ChevronRight className={'ms-1'} />
                    </button>
                ) : (
                    <Publish />
                )}
            </Fragment>
        )
    }

    return (
        <div className="NavBar2 no-gutters">
            <div className="Left col-4 text-left d-flex align-items-center">
                {
                    isPreviewStep || isPricingStep || mappingProductId ? (
                        <span className='PrevStep cursor-pointer flex-center' onClick={handleNavigate}>
                            <span className='Icon'>
                                <ArrowBack />
                            </span>
                            {
                                mappingProductId ? (
                                    <span className={'text-primary'}>Select variant</span>
                                ) : isPreviewStep || isPricingStep ? (
                                    <span className={'text-primary'}>Design</span>
                                ) : (
                                    <a href='/'>Product</a>
                                )
                            }
                        </span>
                    ) : (
                        <a href="/" className='PrevStep cursor-pointer flex-center text-primary text-decoration-none'>
                            <span className='Icon'>
                                <ArrowBack />
                            </span>
                            Product
                        </a>
                    )
                }
                <div className={'vr mx-3'} />
                <h6 className={'fw-500 mb-0'}>{productDetail.title}</h6>
                {/*<ChangeProductButton/>*/}
            </div>

            <div className='Right col-8'>
                <div className={'d-flex justify-content-end'}>
                    {renderSaveButton()}
                </div>
            </div>
        </div>
    )
}

export default NavBar



